import { createI18n, type VueMessageType } from 'vue-i18n';

type LocaleCode = 'en' | 'es' | 'es-mx' | 'fr-ca' | 'pt-br' | (string & {});

type MessagesNamespace = Record<string, VueMessageType>;

type CoreNamespaces = Record<string, MessagesNamespace>;

type LocaleMessageTree = {
  core?: CoreNamespaces;
} & Record<string, MessagesNamespace>;

type Messages = Record<LocaleCode, LocaleMessageTree>;

type JsonModule = { default: MessagesNamespace };

const coreLocales = import.meta.glob<JsonModule>('@nova/core-locales/**/*.json', { eager: true });
const satelliteLocales = import.meta.glob<JsonModule>('@satellite/locales/**/*.json', {
  eager: true
});
const appLocales = import.meta.glob<JsonModule>('@/locales/**/*.json', { eager: true });

function loadLocaleMessages(): Messages {
  const messages: Partial<Messages> = {};

  const all = [
    ...Object.entries(coreLocales).map(([path, mod]) => ({ path, mod, isCore: true })),
    ...Object.entries(satelliteLocales).map(([path, mod]) => ({ path, mod, isCore: false })),
    ...Object.entries(appLocales).map(([path, mod]) => ({ path, mod, isCore: false }))
  ];

  for (const { path, mod, isCore } of all) {
    // .../locales/<locale>/<namespace>.json   (also matches core-locales)
    const m = path.match(/\/(?:core-)?locales\/([a-zA-Z-]+)\/([a-zA-Z-]+)\.json$/);
    if (!m) {
      console.warn('Unmatched locale path:', path);
      continue;
    }
    const [, locale, ns] = m;
    const code = locale as LocaleCode;

    messages[code] ??= {} as LocaleMessageTree;

    if (isCore) {
      messages[code]!.core ??= {} as CoreNamespaces;
      messages[code]!.core![ns] = mod.default;
    } else {
      messages[code]![ns] = mod.default;
    }
  }

  // Alias `es` -> `es-mx` if only es-mx exists
  if (messages['es-mx'] && !messages['es']) {
    messages['es'] = messages['es-mx'];
  }

  return messages as Messages;
}

const i18n = createI18n({
  locale: 'en',
  messages: loadLocaleMessages()
});

export default i18n;
